import React, { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Target,
  X,
  Wallet,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  PiggyBank,
  Rows3,
} from "lucide-react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { api, fmtINR, formatApiError } from "../lib/api";
import MoneyInput from "../components/ui/MoneyInput";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import DateField from "../components/ui/DateField";
import "../styles/date.css";
import GoalFundingModal from "../components/planning/GoalFundingModal";

const FUNDING_BADGE = {
  "On Track":
    "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  "Slightly Behind":
    "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300",
  "At Risk": "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300",
};

const GOAL_CATEGORIES = [
  { value: "retirement", label: "Retirement" },
  { value: "education", label: "Education" },
  { value: "real_estate", label: "Home / Property" },
  { value: "healthcare", label: "Healthcare" },
  { value: "travel", label: "Travel" },
  { value: "vehicle", label: "Vehicle" },
  { value: "wedding", label: "Wedding" },
  { value: "business", label: "Business" },
  { value: "emergency", label: "Emergency Fund" },
  { value: "other", label: "Other" },
];

// Mirrors backend GOAL_CATEGORY_INFLATION — the default inflation each goal type
// is planned at when the user doesn't override it.
const GOAL_CATEGORY_INFLATION = {
  retirement: 6,
  education: 10,
  real_estate: 7,
  wedding: 7,
  vehicle: 5,
  travel: 5,
  business: 6,
  healthcare: 12,
  emergency: 6,
  other: 6,
};

const PAGE_SIZE_OPTIONS = [6, 12, 24];

function Pagination({ total, page, pageSize, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push("…");
      for (
        let i = Math.max(2, safePage - 1);
        i <= Math.min(totalPages - 1, safePage + 1);
        i++
      )
        pages.push(i);
      if (safePage < totalPages - 2) pages.push("…");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span>
          Showing{" "}
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {start + 1}–{Math.min(start + pageSize, total)}
          </span>{" "}
          of{" "}
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {total}
          </span>
        </span>
        <span className="hidden sm:inline text-slate-300 dark:text-slate-700">
          |
        </span>
        <div className="hidden sm:flex items-center gap-1.5">
          <span>Per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage === 1}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {getPageNumbers().map((p, i) =>
          p === "…" ? (
            <span
              key={`e-${i}`}
              className="px-1 text-xs text-slate-400 select-none"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[32px] h-8 rounded-lg text-xs font-medium transition ${safePage === p ? "bg-emerald-500 text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800"}`}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
          disabled={safePage === totalPages}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function Goals() {
  const { viewOnly } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [progressGoal, setProgressGoal] = useState(null);
  const [fundingGoal, setFundingGoal] = useState(null);
  const [planning, setPlanning] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/goals");
      setList(data.goals || []);
      try {
        const pj = await api.get("/planning/goals");
        const map = {};
        (pj.data.goals || []).forEach((g) => (map[g.goal_id] = g));
        setPlanning(map);
      } catch (_) {
        /* planning overlay is optional */
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePageSizeChange = (s) => {
    setPageSize(s);
    setPage(1);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    try {
      await api.delete(`/goals/${id}`);
      toast.success("Goal deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedList = list.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">
            Your dreams, sized
          </p>
          <h1
            data-testid="goals-title"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Goals
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Track milestones and see how close you are to each goal.
          </p>
        </div>
        {!viewOnly && (
          <div className="flex items-center gap-2">
            <Link data-testid="open-bulk-goals" to="/dashboard/bulk-add/goals" className="btn-secondary">
              <Rows3 className="h-4 w-4" /> Bulk add
            </Link>
            <button
              data-testid="open-add-goal"
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="h-4 w-4" /> Add goal
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="card-soft p-12 text-center text-sm text-slate-500">
          Loading…
        </div>
      ) : list.length === 0 ? (
        <div className="card-soft p-12 text-center">
          <Target className="h-10 w-10 text-slate-300 mx-auto" />
          <h3 className="mt-3 font-display font-bold">No goals yet</h3>
          <p className="mt-1 text-sm text-slate-500">
            Set your first financial goal to start tracking progress.
          </p>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-5">
            {pagedList.map((g) => {
              const p = planning[g.id];
              // Funded today = present value of assigned assets + manually
              // tracked savings (at face value). The backend already combines
              // both into reserved_today.
              const hasAssetsReserved = p && (p.assets || []).length > 0;
              const fundedToday = p
                ? p.reserved_today
                : Number(g.current_amount || 0);
              const futureNeed = p
                ? p.future_requirement
                : Number(g.target_amount || 0);
              const projected = p ? p.projected_value : fundedToday;
              const pct = p
                ? Math.min(p.funding_pct, 100)
                : Math.min((fundedToday / (g.target_amount || 1)) * 100, 100);
              const remaining = Math.max(futureNeed - projected, 0);
              return (
                <div
                  key={g.id}
                  data-testid={`goal-card-${g.id}`}
                  className="card-soft p-6"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-widest bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 rounded-full px-2 py-0.5 font-semibold">
                          {g.category || "general"}
                        </span>
                        {planning[g.id]?.funding_band && (
                          <span
                            data-testid={`goal-band-${g.id}`}
                            className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold ${FUNDING_BADGE[planning[g.id].funding_band]}`}
                          >
                            {planning[g.id].funding_band}
                          </span>
                        )}
                      </div>
                      <h3 className="mt-3 font-display text-xl font-bold text-slate-900 dark:text-white">
                        {g.name}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1">
                        Target:{" "}
                        {new Date(g.target_date).toLocaleDateString("en-IN", {
                          year: "numeric",
                          month: "short",
                        })}
                      </p>
                    </div>
                    {!viewOnly && (
                    <div className="flex gap-1">
                      <IconButton
                        tooltip="Reserve assets / funding"
                        data-testid={`fund-goal-${g.id}`}
                        onClick={() => setFundingGoal(g)}
                        className="p-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-500/10 text-slate-500 hover:text-emerald-600"
                      >
                        <PiggyBank className="h-3.5 w-3.5" />
                      </IconButton>
                      {!g.is_completed && (
                        <>
                          <IconButton
                            tooltip="Edit Goal"
                            data-testid={`edit-goal-${g.id}`}
                            onClick={() => {
                              setEditing(g);
                              setShowModal(true);
                            }}
                            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-emerald-600"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </IconButton>
                          <IconButton
                            tooltip="Update Progress"
                            onClick={() => setProgressGoal(g)}
                            className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-500/10 text-slate-500 hover:text-blue-600"
                          >
                            <Wallet className="h-3.5 w-3.5" />
                          </IconButton>
                          <IconButton
                            tooltip="Mark as Completed"
                            onClick={async () => {
                              try {
                                await api.post(`/goals/${g.id}/progress`, {
                                  current_amount: g.target_amount,
                                  is_completed: true,
                                });
                                toast.success("Goal completed");
                                load();
                              } catch (e) {
                                toast.error(formatApiError(e));
                              }
                            }}
                            className="p-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-500/10 text-slate-500 hover:text-emerald-600"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </IconButton>
                        </>
                      )}
                      <IconButton
                        tooltip="Delete Goal"
                        data-testid={`delete-goal-${g.id}`}
                        onClick={() => onDelete(g.id)}
                        className="p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10 text-slate-500 hover:text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <div className="text-xs text-slate-500">
                        {hasAssetsReserved
                          ? "Funded today (savings + reserved)"
                          : "Saved today"}
                      </div>
                      <div className="num-mono text-lg font-bold text-emerald-600">
                        {fmtINR(fundedToday)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">
                        Target (today)
                      </div>
                      <div className="num-mono text-lg font-bold text-slate-900 dark:text-white">
                        {fmtINR(p ? p.target_today : g.target_amount)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                    <span>
                      {g.is_completed
                        ? "Completed"
                        : `${pct.toFixed(1)}% funded`}
                    </span>
                    {p && (
                      <span className="inline-flex items-center gap-1">
                        <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5">
                          {p.inflation_pct}% infl
                        </span>
                      </span>
                    )}
                  </div>

                  {p ? (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-1.5 text-[11px]">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">
                          Future need (inflation-adjusted)
                        </span>
                        <span className="num-mono font-medium text-slate-700 dark:text-slate-300">
                          {fmtINR(futureNeed, { compact: true })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">
                          Projected from reserved assets
                        </span>
                        <span className="num-mono font-medium text-emerald-600">
                          {fmtINR(projected, { compact: true })}
                        </span>
                      </div>
                      {remaining > 0 && p.additional_monthly_investment > 0 && (
                        <div className="flex items-center justify-between text-amber-600">
                          <span>Top-up to get on track</span>
                          <span className="num-mono font-semibold">
                            {fmtINR(p.additional_monthly_investment)}/mo
                          </span>
                        </div>
                      )}
                      {p.manual_saved > 0 && hasAssetsReserved && (
                        <div className="flex items-center justify-between text-slate-400">
                          <span>of which manually tracked</span>
                          <span className="num-mono">
                            {fmtINR(p.manual_saved, { compact: true })}
                          </span>
                        </div>
                      )}
                      {!hasAssetsReserved && (
                        <button
                          onClick={() => setFundingGoal(g)}
                          className="mt-1 text-emerald-600 font-medium hover:underline"
                        >
                          Reserve assets to track this goal in real value →
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-slate-500">
                      {fmtINR(remaining, { compact: true })} to go
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {list.length > pageSize && (
            <Pagination
              total={list.length}
              page={safePage}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </>
      )}

      {showModal && (
        <GoalModal
          goal={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
          }}
        />
      )}
      {progressGoal && (
        <GoalProgressModal
          goal={progressGoal}
          onClose={() => setProgressGoal(null)}
          onSaved={() => {
            setProgressGoal(null);
            load();
          }}
        />
      )}
      {fundingGoal && (
        <GoalFundingModal
          goal={fundingGoal}
          onClose={() => setFundingGoal(null)}
          onChanged={() => load()}
        />
      )}
    </DashboardLayout>
  );
}

function GoalModal({ goal, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: goal?.name || "",
    target_amount: goal?.target_amount || "",
    current_amount: goal?.current_amount || 0,
    target_date: goal?.target_date ? String(goal.target_date).slice(0, 10) : "",
    category: goal?.category || "retirement",
    inflation_rate: goal?.inflation_rate ?? "",
    tracking_frequency: goal?.tracking_frequency || "yearly",
    is_retirement: goal?.is_retirement ?? goal?.category === "retirement",
  });
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        target_amount: Number(String(form.target_amount).replace(/,/g, "")),
        current_amount: Number(
          String(form.current_amount || 0).replace(/,/g, ""),
        ),
        target_date: form.target_date,
        tracking_frequency: form.tracking_frequency,
        is_retirement: !!form.is_retirement || form.category === "retirement",
      };
      if (form.inflation_rate !== "" && form.inflation_rate !== null) {
        payload.inflation_rate = Number(form.inflation_rate);
      }
      if (goal?.id) await api.put(`/goals/${goal.id}`, payload);
      else await api.post("/goals", payload);
      toast.success(goal ? "Updated" : "Goal added");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto p-4">
      <div className="min-h-full flex items-center justify-center py-10">
        <div className="w-full max-w-2xl card-soft p-6 overflow-visible">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
              {goal ? "Edit goal" : "Add goal"}
            </h2>
            <button
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Name *
              </label>
              <input
                data-testid="goal-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-soft"
                placeholder="e.g. Retirement Corpus"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Category
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="input-soft"
              >
                {GOAL_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Inflation %
                </label>
                <input
                  data-testid="goal-inflation-input"
                  type="number"
                  step="0.5"
                  value={form.inflation_rate}
                  onChange={(e) =>
                    setForm({ ...form, inflation_rate: e.target.value })
                  }
                  className="input-soft"
                  placeholder={`Default ${
                    GOAL_CATEGORY_INFLATION[form.category] ?? 6
                  }% for ${
                    GOAL_CATEGORIES.find((c) => c.value === form.category)
                      ?.label || "this goal"
                  }`}
                />
                <p className="text-[11px] text-slate-400">
                  Leave blank to use the{" "}
                  {GOAL_CATEGORY_INFLATION[form.category] ?? 6}% default for
                  this goal type. The target inflates at this rate to its future
                  value.
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Tracking frequency
                </label>
                <select
                  value={form.tracking_frequency}
                  onChange={(e) =>
                    setForm({ ...form, tracking_frequency: e.target.value })
                  }
                  className="input-soft"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <Checkbox
                data-testid="goal-retirement-toggle"
                checked={!!form.is_retirement}
                onCheckedChange={(checked) =>
                  setForm({ ...form, is_retirement: checked === true })
                }
              />

              <span>
                This is my retirement goal (only retirement-reserved assets fund
                it)
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Target Amount *
                </label>
                <MoneyInput
                  data-testid="goal-target-input"
                  required
                  value={form.target_amount}
                  onValueChange={(raw) =>
                    setForm({ ...form, target_amount: raw })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Current Saved Amount
                </label>
                <MoneyInput
                  value={form.current_amount}
                  onValueChange={(raw) =>
                    setForm({ ...form, current_amount: raw })
                  }
                  placeholder="0"
                />
              </div>
              <div className="space-y-1.5 relative col-span-2">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Target Date *
                </label>
                <DateField
                  value={form.target_date}
                  onChange={(v) => setForm({ ...form, target_date: v })}
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="goal-save-btn"
                disabled={saving}
                className="btn-primary flex-1"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function GoalProgressModal({ goal, onClose, onSaved }) {
  const [amount, setAmount] = useState(goal.current_amount || 0);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    try {
      setSaving(true);
      const numericAmount = Number(String(amount).replace(/,/g, ""));
      await api.post(`/goals/${goal.id}/progress`, {
        current_amount: numericAmount,
      });
      toast.success("Goal progress updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const pct = Math.min(
    ((Number(String(amount || 0).replace(/,/g, "")) || 0) /
      goal.target_amount) *
      100,
    100,
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto p-4">
      <div className="min-h-full flex items-center justify-center py-10">
        <div className="w-full max-w-lg card-soft p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
                Update Progress
              </h2>
              <p className="text-sm text-slate-500 mt-1">{goal.name}</p>
            </div>
            <button
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-4">
            <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500">Current Saved</div>
                  <div className="text-xl font-bold text-emerald-600 num-mono">
                    {fmtINR(Number(String(amount || 0).replace(/,/g, "")))}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Target</div>
                  <div className="text-xl font-bold text-slate-900 dark:text-white num-mono">
                    {fmtINR(goal.target_amount)}
                  </div>
                </div>
              </div>
              <div className="mt-4 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {pct.toFixed(1)}% completed
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Update Current Amount
              </label>
              <MoneyInput
                value={amount}
                onValueChange={(raw) => setAmount(raw)}
                className="mt-2"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving}
                className="btn-primary flex-1"
              >
                {saving ? "Saving..." : "Update Progress"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function IconButton({ children, tooltip, className = "", ...props }) {
  return (
    <div className="relative group">
      <button {...props} className={className}>
        {children}
      </button>
      <div className="pointer-events-none absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
        <div className="whitespace-nowrap rounded-lg bg-slate-900 text-white text-[11px] px-2 py-1 shadow-xl">
          {tooltip}
        </div>
      </div>
    </div>
  );
}
